import { useNavigate } from '@hulu/react-router-dom';
import { TextField } from '@hulu/react-style-components';
import { Field, FieldProps, Form, Formik } from 'formik';
import React from 'react';

import { PasswordInput } from '../../../components';
import { BlueButton, StyledLink, StyledTextFieldWrapper } from '../../../components/BlueTheme';
import { LINKS } from '../../../components/Header/constants';
import MailtoLink from '../../../components/MailtoLink';
import { Message } from '../../../components/MFA/MultiFactorAuthEnabled/styled';
import StepHeader from '../../../components/MFA/StepHeader/StepHeader';
import { RoutePath } from '../../../constants';
import { defaultSubTitle } from '../../../hooks/useMFA/constants';
import { ElementWrapper, PasswordFieldContainerStyled, StyledBottomContent, TextFieldContainerStyled } from '../styled';
import { EMAIL, LOGIN_FORM_INITIAL_VALUES, LoginFormValidationSchema, PASSWORD } from './constants';
import { ForgotPasswordContent } from './styled';
import { LoginForm } from './types';

type Props = {
  loading: boolean;
  onLogin: (email: string, password: string) => void;
};

const EmailPasswordStep = ({ loading, onLogin }: Props): JSX.Element => {
  const navigate = useNavigate();

  const moveToResendInvite = (): void => {
    navigate(RoutePath.RESEND_INVITE);
  };

  const moveToSighUp = (): void => {
    window.open(LINKS.signUp, '_blank');
  };

  const moveToForgotPassword = (): void => {
    navigate(RoutePath.FORGOT_PASSWORD);
  };

  const handleSubmit = ({ email, password }: LoginForm): void => {
    onLogin(email, password);
  };

  return (
    <>
      <StepHeader subTitle={defaultSubTitle} />
      <Formik<LoginForm>
        initialValues={LOGIN_FORM_INITIAL_VALUES}
        onSubmit={handleSubmit}
        validationSchema={LoginFormValidationSchema}
        initialErrors={LOGIN_FORM_INITIAL_VALUES}
      >
        {({ isValid }): JSX.Element => (
          <Form>
            <TextFieldContainerStyled data-testid="fieldEmailWrapper">
              <Field name={EMAIL}>
                {({ field, meta }: FieldProps<string, LoginForm>): JSX.Element => (
                  <StyledTextFieldWrapper>
                    <TextField
                      {...field}
                      label={'Email'}
                      errMsg={meta.touched && meta.error ? meta.error : undefined}
                      placeholder="Enter your email address"
                      data-testid="fieldEmail"
                    />
                  </StyledTextFieldWrapper>
                )}
              </Field>
            </TextFieldContainerStyled>

            <PasswordFieldContainerStyled data-testid="fieldPasswordWrapper">
              <Field name={PASSWORD}>
                {({ field, meta }: FieldProps<string, LoginForm>): JSX.Element => (
                  <StyledTextFieldWrapper>
                    <PasswordInput
                      {...field}
                      labelText="Password"
                      errorMessage={(meta.touched && meta.error) || ''}
                      placeholder="Enter your password"
                      dataTestId="fieldPassword"
                    />
                  </StyledTextFieldWrapper>
                )}
              </Field>
            </PasswordFieldContainerStyled>

            <ForgotPasswordContent>
              <StyledLink onClick={moveToForgotPassword} data-testid="forgotPasswordButton">
                Forgot password?
              </StyledLink>
            </ForgotPasswordContent>

            <ElementWrapper>
              <BlueButton
                name="Continue"
                type="submit"
                disabled={!isValid || loading}
                isLoading={loading}
                data-testid="loginFormSubmitButton"
              />
            </ElementWrapper>
          </Form>
        )}
      </Formik>
      <StyledBottomContent>
        <div>
          Can’t find your invite?{' '}
          <StyledLink onClick={moveToResendInvite} data-testid="canNotFindYourInviteButton">
            Resend
          </StyledLink>
          <Message>
            Don't have an account? <StyledLink onClick={moveToSighUp}>Sign Up</StyledLink>
          </Message>
          <Message>
            Need Help? <MailtoLink mail={'dcm-support@email.disney.com'} text="dcm-support@email.disney.com" />
          </Message>
        </div>
      </StyledBottomContent>
    </>
  );
};

export default EmailPasswordStep;
